import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { X, FileSpreadsheet, Printer, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

const FORMATS = [
  { key: 'excel', label: 'Excel', icon: FileSpreadsheet, description: 'Download as .xlsx file' },
  { key: 'pdf',   label: 'PDF',   icon: Printer,         description: 'Print / Save as PDF' },
  { key: 'web',   label: 'Web',   icon: Globe,           description: 'Open printable web page' },
];

function buildRows(records) {
  return records.map(r => ({
    'Employee Code':  r.employee_code || '',
    'Name':          r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim(),
    'Department':    r.department?.name || r.department || '',
    'Designation':   r.designation || '',
    'Date':          r.date || '',
    'Status':        r.status || '',
    'Check In':      r.check_in || '',
    'Check Out':     r.check_out || '',
    'Hours Worked':  r.hours_worked || r.work_hours || '',
    'Leave Type':    r.leave_type || '',
    'Method':        r.method || '',
    'Late By (min)': r.late_by_minutes || r.late_by || '',
  }));
}

function buildPrintHtml(records, filterSummary, companyName) {
  const rows = buildRows(records);
  const headers = Object.keys(rows[0] || {});
  const trRows = rows.map(r =>
    `<tr>${headers.map(h => `<td>${r[h] ?? ''}</td>`).join('')}</tr>`
  ).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Attendance Register — ${companyName}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #111; }
    h2 { font-size: 16px; margin: 0 0 4px; }
    .meta { color: #666; font-size: 10px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f0f0f0; border: 1px solid #ccc; padding: 5px 8px; text-align: left; font-size: 10px; text-transform: uppercase; }
    td { border: 1px solid #ddd; padding: 4px 8px; }
    tr:nth-child(even) { background: #fafafa; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h2>Attendance Register — ${companyName}</h2>
  <div class="meta">${filterSummary || 'All employees'} &nbsp;·&nbsp; ${records.length} records &nbsp;·&nbsp; Generated ${new Date().toLocaleString('en-IN')}</div>
  <table>
    <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${trRows}</tbody>
  </table>
</body>
</html>`;
}

export function AttendanceExportModal({ onClose, records, filterSummary, companyName = 'AttendX' }) {
  const [fmt, setFmt] = useState('excel');
  const iframeRef = useRef(null);

  const handleExport = () => {
    if (fmt === 'excel') {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(buildRows(records));
      XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
      XLSX.writeFile(wb, `attendx-attendance-${new Date().toISOString().slice(0, 10)}.xlsx`);
      onClose();
    } else if (fmt === 'pdf') {
      const html = buildPrintHtml(records, filterSummary, companyName);
      const iframe = iframeRef.current;
      iframe.srcdoc = html;
      iframe.onload = () => { iframe.contentWindow.print(); };
    } else {
      const html = buildPrintHtml(records, filterSummary, companyName);
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-[16px] max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h2 className="text-[16px] font-bold text-[var(--text-primary)]">Export Attendance</h2>
          <button onClick={onClose} className="p-1.5 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-[10px] bg-[var(--bg-muted)] p-3 text-[12px] text-[var(--text-secondary)]">
            <p className="font-semibold text-[var(--text-tertiary)] uppercase tracking-wide text-[11px] mb-1">Active Filters</p>
            {filterSummary || 'None — exporting all records'}
            <p className="mt-1"><strong>{records.length}</strong> record{records.length !== 1 ? 's' : ''} will be exported</p>
          </div>

          <div className="space-y-1.5">
            <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Format</p>
            <div className="grid grid-cols-3 gap-2">
              {FORMATS.map(f => (
                <button key={f.key} onClick={() => setFmt(f.key)}
                  className={cn(
                    'flex flex-col items-center gap-1.5 py-3 px-2 rounded-[12px] border-2 transition-all text-center',
                    fmt === f.key ? 'border-[var(--accent)] bg-[var(--accent)]/5' : 'border-[var(--border)] hover:border-[var(--accent)]/40'
                  )}>
                  <f.icon size={20} className={fmt === f.key ? 'text-[var(--accent)]' : 'text-[var(--text-secondary)]'}/>
                  <span className={`text-[12px] font-bold ${fmt === f.key ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]'}`}>{f.label}</span>
                  <span className="text-[10px] text-[var(--text-tertiary)] leading-tight">{f.description}</span>
                </button>
              ))}
            </div>
          </div>

          <Button onClick={handleExport} disabled={!records.length} className="w-full">
            {fmt === 'excel' ? 'Download Excel' : fmt === 'pdf' ? 'Open Print Dialog' : 'Open Web Page'}
          </Button>
        </div>

        {/* Hidden iframe for PDF printing */}
        <iframe ref={iframeRef} className="hidden" title="print-frame"/>
      </div>
    </div>
  );
}
